import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Search,
  ChevronDown,
  ChevronUp,
  Filter,
  ArrowLeft,
  AlertTriangle,
  X,
  FileText,
  Check,
} from "lucide-react";
import ReferralService from "../../../services/api/admin.referral.service";
import ReferralRewardConfig from "./ReferralRewardConfig";
import { notificationService } from "../../../services/api/notification.service";

const VIEWED_REFERRALS_KEY = "viewedReferralIds";

const ReferralQualification = ({
  trackingId,
  onBack,
  onQualificationComplete,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);
  const [qualificationData, setQualificationData] = useState(null);

  // Use ref to track if API call has been made for this trackingId
  const hasCalledApiRef = useRef(false);
  const currentTrackingIdRef = useRef(null);

  // Use useCallback to memoize the function and prevent unnecessary re-renders
  const processQualification = useCallback(async () => {
    // Don't proceed if trackingId is not available
    if (!trackingId) {
      console.error("No tracking ID provided");
      setError("No referral ID provided. Please try again.");
      setLoading(false);
      return;
    }

    // Prevent duplicate calls for the same trackingId
    if (
      hasCalledApiRef.current &&
      currentTrackingIdRef.current === trackingId
    ) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setErrorDetails(null);

      // Mark that we're making the API call
      hasCalledApiRef.current = true;
      currentTrackingIdRef.current = trackingId;

      // Use the referral service to qualify the referral
      const response = await ReferralService.qualifyReferral(trackingId);

      setQualificationData(response);
      setLoading(false);

      // If qualification was successful, refresh the list view
      if (response.success) {
        onQualificationComplete();
      }
    } catch (err) {
      console.error("Error processing qualification:", err);

      // Handle API error response
      if (err.response && err.response.data) {
        console.error("Error details:", err.response.data);

        // Set the main error message
        setError(
          err.response.data.message || "Failed to process qualification"
        );

        // Store error details if available
        if (err.response.data.error) {
          setErrorDetails(err.response.data.error);
        }
      } else {
        // Generic error handling
        setError(`Failed to process qualification: ${err.message}`);
      }

      setLoading(false);
    }
  }, [trackingId, onQualificationComplete]);

  useEffect(() => {
    // Reset the API call tracking when trackingId changes
    if (currentTrackingIdRef.current !== trackingId) {
      hasCalledApiRef.current = false;
      currentTrackingIdRef.current = null;
      setQualificationData(null);
      setError(null);
      setErrorDetails(null);
    }

    // Only run processQualification if trackingId exists and we haven't made the call yet
    if (trackingId && !hasCalledApiRef.current) {
      processQualification();
    }
  }, [trackingId, processQualification]);

  // Render error details UI
  const renderErrorDetails = () => {
    if (!errorDetails) return null;

    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
        <h3 className="text-lg font-medium text-red-800">Error Details</h3>

        {errorDetails.code && (
          <div className="mt-2">
            <span className="font-medium">Error Code:</span> {errorDetails.code}
          </div>
        )}

        {errorDetails.details && (
          <div className="mt-2">
            <h4 className="font-medium">Details:</h4>
            <ul className="list-disc pl-5 mt-1">
              {errorDetails.details.message && (
                <li className="text-red-700">{errorDetails.details.message}</li>
              )}
              {errorDetails.details.referrerFound !== undefined && (
                <li className="text-red-700 flex items-center">
                  Referrer status:
                  <span className="ml-1 flex items-center">
                    {errorDetails.details.referrerFound ? (
                      <span className="text-green-600 flex items-center">
                        <span className="inline-block w-4 h-4 bg-green-100 rounded-full text-green-700 flex items-center justify-center mr-1">
                          ✓
                        </span>{" "}
                        Found
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center">
                        <span className="inline-block w-4 h-4 bg-red-100 rounded-full text-red-700 flex items-center justify-center mr-1">
                          ✗
                        </span>{" "}
                        Not found
                      </span>
                    )}
                  </span>
                </li>
              )}
              {errorDetails.details.refereeFound !== undefined && (
                <li className="text-red-700 flex items-center">
                  Referee status:
                  <span className="ml-1 flex items-center">
                    {errorDetails.details.refereeFound ? (
                      <span className="text-green-600 flex items-center">
                        <span className="inline-block w-4 h-4 bg-green-100 rounded-full text-green-700 flex items-center justify-center mr-1">
                          ✓
                        </span>{" "}
                        Found
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center">
                        <span className="inline-block w-4 h-4 bg-red-100 rounded-full text-red-700 flex items-center justify-center mr-1">
                          ✗
                        </span>{" "}
                        Not found
                      </span>
                    )}
                  </span>
                </li>
              )}
            </ul>
          </div>
        )}

        {errorDetails.code === "USER_NOT_FOUND" && (
          <div className="mt-4 p-3 bg-red-100 rounded-md">
            <p className="text-red-800 font-medium">Action Required:</p>
            <p className="text-red-700">
              The referral cannot be processed because one or both users are
              missing from the system. Please verify that both users exist in
              the database.
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <button
          onClick={onBack}
          className="mr-4 p-2 rounded-full hover:bg-gray-100"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          Referral Qualification Process
        </h1>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
        </div>
      ) : error ? (
        <div className="space-y-4">
          <div className="bg-red-100 p-4 rounded-md text-red-700 flex items-start">
            <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">{error}</p>
              <p className="text-sm mt-1">
                Please check the details below or return to the referral list.
              </p>
            </div>
          </div>

          {renderErrorDetails()}

          <div className="mt-4">
            <button
              onClick={onBack}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Back to Referral List
            </button>
          </div>
        </div>
      ) : qualificationData ? (
        <div>
          <div
            className={`p-4 rounded-lg mb-6 ${
              qualificationData.success ? "bg-green-100" : "bg-yellow-100"
            }`}
          >
            <p
              className={`text-lg font-medium ${
                qualificationData.success ? "text-green-800" : "text-yellow-800"
              }`}
            >
              {qualificationData.message}
            </p>
          </div>

          {qualificationData.data && (
            <div className="space-y-6">
              {/* Tracking Information */}
              <div className="border rounded-lg p-4">
                <h2 className="text-lg font-medium mb-3">
                  Tracking Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Referral Code</p>
                    <p className="font-medium">
                      {qualificationData.data.tracking?.referralCode || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    {qualificationData.data.tracking?.status ? (
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          qualificationData.data.tracking.status === "CONVERTED"
                            ? "bg-green-100 text-green-800"
                            : qualificationData.data.tracking.status ===
                              "QUALIFIED"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {qualificationData.data.tracking.status}
                      </span>
                    ) : (
                      <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                        N/A
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* KYC Status */}
              <div className="border rounded-lg p-4">
                <h2 className="text-lg font-medium mb-3">KYC Status</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Referrer</p>
                    {qualificationData.data.kycStatus?.referrer ? (
                      <>
                        <p className="font-medium">
                          {qualificationData.data.kycStatus.referrer.name ||
                            "Name not available"}
                        </p>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            qualificationData.data.kycStatus.referrer
                              .isKycVerified
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {qualificationData.data.kycStatus.referrer
                            .isKycVerified
                            ? "Verified"
                            : "Pending"}
                        </span>
                      </>
                    ) : (
                      <div className="flex items-center text-red-600">
                        <X className="w-4 h-4 mr-1" />
                        <span>Referrer not found</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Referee</p>
                    {qualificationData.data.kycStatus?.referee ? (
                      <>
                        <p className="font-medium">
                          {qualificationData.data.kycStatus.referee.name ||
                            "Name not available"}
                        </p>
                        <span
                          className={`px-2 py-1 rounded-full text-xs ${
                            qualificationData.data.kycStatus.referee
                              .isKycVerified
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {qualificationData.data.kycStatus.referee
                            .isKycVerified
                            ? "Verified"
                            : "Pending"}
                        </span>
                      </>
                    ) : (
                      <div className="flex items-center text-red-600">
                        <X className="w-4 h-4 mr-1" />
                        <span>Referee not found</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Missing Users Information (if any) */}
              {(!qualificationData.data.kycStatus?.referrer ||
                !qualificationData.data.kycStatus?.referee) &&
                !errorDetails && (
                  <div className="border border-red-200 rounded-lg p-4 bg-red-50">
                    <h2 className="text-lg font-medium mb-3 text-red-800">
                      Missing Users
                    </h2>
                    <div className="space-y-2">
                      {!qualificationData.data.kycStatus?.referrer && (
                        <div className="px-3 py-2 bg-red-100 rounded-md flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                          <p className="text-red-700">
                            Referrer information is missing
                          </p>
                        </div>
                      )}
                      {!qualificationData.data.kycStatus?.referee && (
                        <div className="px-3 py-2 bg-red-100 rounded-md flex items-center">
                          <AlertTriangle className="w-4 h-4 mr-2 text-red-600" />
                          <p className="text-red-700">
                            Referee information is missing
                          </p>
                        </div>
                      )}
                      <div className="px-3 py-2 mt-2 bg-yellow-100 rounded-md">
                        <p className="text-yellow-800">
                          This referral cannot be processed due to missing user
                          information.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Reward Information (if successful) */}
              {qualificationData.success && qualificationData.data.reward && (
                <div className="border rounded-lg p-4 bg-green-50">
                  <h2 className="text-lg font-medium mb-3">
                    Reward Information
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Amount</p>
                      <p className="font-medium">
                        ₹{qualificationData.data.reward.amount}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Transaction ID</p>
                      <p className="font-medium">
                        {qualificationData.data.reward.transactionId || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Pending Verifications (if not qualified) */}
              {!qualificationData.success &&
                qualificationData.data.pendingVerifications && (
                  <div className="border rounded-lg p-4 bg-yellow-50">
                    <h2 className="text-lg font-medium mb-3">
                      Pending Verifications
                    </h2>
                    <div className="space-y-2">
                      {qualificationData.data.pendingVerifications.map(
                        (user, index) => (
                          <div
                            key={`pending-${index}`}
                            className="px-3 py-2 bg-yellow-100 rounded-md"
                          >
                            <p className="capitalize">
                              {user} KYC verification pending
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}
            </div>
          )}

          <div className="mt-8 flex justify-end">
            <button
              onClick={onBack}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Back to Referral List
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Main ReferralList component
const ReferralList = () => {
  // State for managing view
  const [view, setView] = useState("list");
  const [selectedReferralId, setSelectedReferralId] = useState(null);
  const [showRewardConfig, setShowRewardConfig] = useState(false);

  // State for storing referrals data and UI controls
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Sorting state
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Summary state
  const [summary, setSummary] = useState({
    totalReferrals: 0,
    pending: 0, // Changed from pendingKyc to pending
    qualified: 0,
    converted: 0,
  });

  // Filter state
  const [filters, setFilters] = useState({
    status: "",
    startDate: "",
    endDate: "",
    search: "",
  });

  // Toggle filter panel
  const [showFilters, setShowFilters] = useState(false);

  // State to track mark as read operation
  const [markingAsRead, setMarkingAsRead] = useState(false);
  // In your component state
  const [viewedReferralIds, setViewedReferralIds] = useState(() => {
    const stored = localStorage.getItem(VIEWED_REFERRALS_KEY);
    return stored ? JSON.parse(stored) : [];
  });

  // Helper function to check if referral is from last 24 hours
  const isWithin24Hours = (dateString) => {
    const referralDate = new Date(dateString);
    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    return referralDate >= twentyFourHoursAgo;
  };

  // Check if there are any new referrals (within 24 hours and not viewed)
  const hasNewReferrals = () => {
    return referrals.some(
      (referral) => isWithin24Hours(referral.createdAt) && !referral.isViewed
    );
  };

  // Update handleMarkAsRead to persist to localStorage
  const handleMarkAsRead = async () => {
    if (markingAsRead) return;
    setMarkingAsRead(true);
    try {
      await notificationService.markCategoryAsRead("referral");
      const newViewed = referrals
        .filter((r) => isWithin24Hours(r.createdAt))
        .map((r) => r.id);
      const updatedViewed = Array.from(
        new Set([...viewedReferralIds, ...newViewed])
      );
      setViewedReferralIds(updatedViewed);
      localStorage.setItem(VIEWED_REFERRALS_KEY, JSON.stringify(updatedViewed));
      setReferrals((prev) =>
        prev.map((r) =>
          isWithin24Hours(r.createdAt) ? { ...r, isViewed: true } : r
        )
      );
    } catch (err) {
      setError("Failed to mark referrals as read. Please try again.");
      setTimeout(() => setError(null), 5000);
    } finally {
      setMarkingAsRead(false);
    }
  };

  // When fetching referrals, merge with localStorage viewed IDs
  const fetchReferrals = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await ReferralService.getReferrals(
        page,
        limit,
        sortBy,
        sortOrder,
        filters
      );

      // Merge with localStorage viewed IDs
      const referralsWithStatus = data.referrals.map((referral) => ({
        ...referral,
        isViewed:
          viewedReferralIds.includes(referral.id) || referral.isViewed || false,
      }));

      setReferrals(referralsWithStatus);
      setTotalRecords(data.pagination.totalRecords);
      setTotalPages(data.pagination.totalPages);
      setSummary(data.summary);
    } catch (err) {
      setError("Failed to fetch referrals");
      console.error("Error fetching referrals:", err);
    } finally {
      setLoading(false);
    }
  };

  // Add viewedReferralIds as a dependency to useEffect for fetchReferrals
  useEffect(() => {
    if (view === "list") {
      fetchReferrals();
    }
    // eslint-disable-next-line
  }, [page, limit, view, viewedReferralIds]);

  // Refetch when sorting or filtering changes
  useEffect(() => {
    if (view === "list") {
      fetchReferrals();
    }
  }, [sortBy, sortOrder]);

  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Apply filters
  const handleApplyFilters = (e) => {
    e.preventDefault();
    setPage(1); // Reset to first page when filtering
    fetchReferrals();
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      status: "",
      startDate: "",
      endDate: "",
      search: "",
    });
    setPage(1);
    fetchReferrals();
  };

  // Handle sort toggle
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
    // fetchReferrals will be called by useEffect
  };

  // Handle proceed button click
  const handleProceed = async (referralId) => {
    if (!referralId) {
      console.error("No referral ID provided to handleProceed");
      return;
    }

    setSelectedReferralId(referralId);
    setView("qualification");
  };

  // Handle back to list
  const handleBackToList = () => {
    setView("list");
    setSelectedReferralId(null);
    // Refresh data when returning to list
    fetchReferrals();
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Format time for 24-hour referrals
  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ChevronUp className="inline w-4 h-4" />
    ) : (
      <ChevronDown className="inline w-4 h-4" />
    );
  };

  // Get status badge color based on status
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "PENDING":
        return "bg-yellow-100 text-yellow-800";
      case "QUALIFIED":
        return "bg-blue-100 text-blue-800";
      case "CONVERTED":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const handleRewardConfigClick = () => {
    setShowRewardConfig(true);
  };

  // Conditional rendering based on view
  if (view === "qualification") {
    return (
      <ReferralQualification
        trackingId={selectedReferralId}
        onBack={handleBackToList}
        onQualificationComplete={() => {
          // Refresh data when returning to list view
          setView("list");
          setSelectedReferralId(null);
        }}
      />
    );
  }

  // For reward config, we'll just show a placeholder since the real component is elsewhere
  if (showRewardConfig) {
    return (
      <div className="p-6">
        <div className="flex items-center mb-6">
          <button
            onClick={() => setShowRewardConfig(false)}
            className="mr-4 p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold text-gray-800">
            Referral Reward Configuration
          </h1>
        </div>

        <ReferralRewardConfig onBack={handleBackToList} />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Updated Filter Button to be more mobile-friendly */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">
          Referral Management
        </h1>
        <div className="flex flex-wrap gap-2 w-full lg:w-auto">
          {/* Mark as Read Button - only show if there are new referrals */}
          {hasNewReferrals() && (
            <button
              onClick={handleMarkAsRead}
              disabled={markingAsRead}
              className={`flex items-center px-4 py-2 ${
                markingAsRead
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              } text-white rounded-md`}
            >
              {markingAsRead ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Marking...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Mark as Read
                </>
              )}
            </button>
          )}

          {/* New reward config button */}
          <button
            onClick={handleRewardConfigClick}
            className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            <FileText className="w-4 h-4 mr-2" />
            Reward Config
          </button>

          {/* Filter button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center px-4 py-2 ${
              showFilters ? "bg-gray-200" : "bg-gray-100"
            } rounded-md hover:bg-gray-200`}
          >
            <Filter className="w-4 h-4 mr-2" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Updated Search Bar to be more mobile-friendly */}
      <div className="mb-6 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <input
            type="text"
            name="search"
            value={filters.search}
            onChange={handleFilterChange}
            placeholder="Search by referral code or user..."
            className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
        </div>
        <button
          onClick={handleApplyFilters}
          className="w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Search
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg shadow">
          <div className="text-blue-700 text-lg font-semibold">
            Total Referrals
          </div>
          <div className="text-2xl font-bold">{summary.totalReferrals}</div>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg shadow">
          <div className="text-yellow-700 text-lg font-semibold">Pending</div>
          <div className="text-2xl font-bold">{summary.pending}</div>
        </div>

        <div className="bg-indigo-50 p-4 rounded-lg shadow">
          <div className="text-indigo-700 text-lg font-semibold">Qualified</div>
          <div className="text-2xl font-bold">{summary.qualified}</div>
        </div>

        <div className="bg-green-50 p-4 rounded-lg shadow">
          <div className="text-green-700 text-lg font-semibold">Converted</div>
          <div className="text-2xl font-bold">{summary.converted}</div>
        </div>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h2 className="text-lg font-medium mb-4">Advanced Filters</h2>
          <form
            onSubmit={handleApplyFilters}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="PENDING">Pending</option>
                <option value="QUALIFIED">Qualified</option>
                <option value="CONVERTED">Converted</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="w-full border rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="md:col-span-2 flex justify-end space-x-2">
              <button
                type="button"
                onClick={handleResetFilters}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Reset
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Referrals Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
          </div>
        ) : error ? (
          <div className="bg-red-100 p-4 rounded-md text-red-700 mb-4">
            {error}
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("referralCode")}
                >
                  Referral Code {renderSortIcon("referralCode")}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                >
                  Referrer
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                >
                  Referee
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("status")}
                >
                  Status {renderSortIcon("status")}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("createdAt")}
                >
                  Date {renderSortIcon("createdAt")}
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {referrals.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No referrals found
                  </td>
                </tr>
              ) : (
                referrals.map((referral, index) => {
                  const isNew24Hours =
                    isWithin24Hours(referral.createdAt) && !referral.isViewed;

                  // FIXED: Create unique key using referral.id + index as fallback
                  const uniqueKey = referral.id || referral.trackingId || `referral-${index}`;

                  return (
                    <tr
                      key={uniqueKey}
                      className={`hover:bg-gray-50 ${
                        isNew24Hours
                          ? "bg-gradient-to-r from-blue-50 to-white border-l-4 border-blue-400"
                          : ""
                      }`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="text-sm font-medium text-gray-900">
                            {referral.referralCode}
                          </div>
                          {/* Blue dot indicator for new referrals within 24 hours */}
                          {isNew24Hours && (
                            <div className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 animate-pulse"></span>
                              <span className="text-xs text-blue-600 font-medium">
                                NEW
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {referral.referrer?.name || "Unknown"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {referral.referrer?.email || "No email"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {referral.referee?.name || "Unknown"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {referral.referee?.email || "No email"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                            referral.status
                          )}`}
                        >
                          {referral.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {isNew24Hours
                          ? formatDateTime(referral.createdAt)
                          : formatDate(referral.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleProceed(referral.trackingId)}
                          className={`${
                            isNew24Hours
                              ? "text-blue-600 hover:text-blue-900 font-semibold"
                              : "text-blue-600 hover:text-blue-900"
                          }`}
                        >
                          Process
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination Controls */}
      {!loading && referrals.length > 0 && (
        <div className="flex justify-between items-center mt-6">
          <div className="text-sm text-gray-700">
            Showing{" "}
            <span className="font-medium">{(page - 1) * limit + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(page * limit, totalRecords)}
            </span>{" "}
            of <span className="font-medium">{totalRecords}</span> referrals
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className={`px-3 py-1 border rounded-md ${
                page === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className={`px-3 py-1 border rounded-md ${
                page === totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralList;